import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { useSelectorProps } from 'hooks';
import { getLocaleUrlPath } from 'utils';

import { getPageIsHidingFromCrawlers } from 'selectors';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  ogImageNbuUk,
  ogImageNbuRu,
  ogImageMbUk,
  ogImageMbRu,
  ogImageConverterUk,
  ogImageConverterRu,
  ogImageHomeUk,
  ogImageHomeRu,
} from '../../assets/images';

const getOgImage = (location, locale) => {
  const isMbPage = location.pathname.includes('/mb/');
  const isNbuPage = location.pathname.includes('/nbu/');
  const isConverterPage = location.pathname.includes('/converter/');
  if (isMbPage) {
    return `${process.env.APP_MINFIN_API_DOMAIN}${
      locale === 'ru' ? ogImageMbRu : ogImageMbUk
    }`;
  }
  if (isNbuPage) {
    return `${process.env.APP_MINFIN_API_DOMAIN}${
      locale === 'ru' ? ogImageNbuRu : ogImageNbuUk
    }`;
  }
  if (isConverterPage) {
    return `${process.env.APP_MINFIN_API_DOMAIN}${
      locale === 'ru' ? ogImageConverterRu : ogImageConverterUk
    }`;
  }
  return `${process.env.APP_MINFIN_API_DOMAIN}${
    locale === 'ru' ? ogImageHomeRu : ogImageHomeUk
  }`;
};

const PageHelmet = memo(({ title, description, meta, ...props }) => {
  const { locale } = useIntl();
  const { location } = useSelectorProps();
  const { sourceCurrency, targetCurrency, val1 } = useParams();
  const hasCustomOgImage = meta.some(
    item => item?.property === 'og:image' && item?.content
  );
  const hasCustomOgDescription = meta.some(
    item => item?.property === 'og:description' && item?.content
  );
  const hasCustomTwitterImage = meta.some(
    item => item?.name === 'twitter:image' && item?.content
  );
  const hasCustomTwitterDescription = meta.some(
    item => item?.name === 'twitter:description' && item?.content
  );

  const isHidingFromCrawlers = useSelector(state => {
    return getPageIsHidingFromCrawlers(state, {
      match: { params: { sourceCurrency, targetCurrency, val1 } },
    });
  });

  return (
    <Helmet
      title={title}
      meta={[
        {
          name: 'description',
          content: description,
        },
        {
          property: 'og:title',
          content: title,
        },
        {
          property: 'og:description',
          content: hasCustomOgDescription ? null : description,
        },
        {
          property: 'og:type',
          content: 'website',
        },
        {
          property: 'og:url',
          content: `${process.env.APP_MINFIN_API_DOMAIN}${getLocaleUrlPath(
            locale
          )}/currency${location.pathname}`,
        },
        {
          property: 'og:image',
          content: hasCustomOgImage ? null : getOgImage(location, locale),
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          name: 'twitter:title',
          content: title,
        },
        {
          name: 'twitter:description',
          content: hasCustomTwitterDescription ? null : description,
        },
        {
          name: 'twitter:image',
          content:
            hasCustomTwitterImage || hasCustomOgImage
              ? null
              : getOgImage(location, locale),
        },
        isHidingFromCrawlers && {
          name: 'robots',
          content: 'noindex, nofollow',
        },
        ...meta,
      ].filter(item => item && item.content !== null)}
      {...props}
    />
  );
});

PageHelmet.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
};

PageHelmet.defaultProps = {
  title: '',
  description: '',
  meta: [],
};

export default PageHelmet;
